import React from 'react';
import { NumberInputDisplay } from '../../Shared/Scripts/Components/ValgDisplayFunctions';
import VeilederStegComponent, { VeilederStegBaseProps } from '../../Shared/Scripts/Components/VeilederStegComponent';
import { connect } from 'react-redux';
import { setCollapsed } from '../../Shared/Scripts/Store/StegVisning/Actions';
import contentResolver from '../../Shared/Scripts/Utilities/EpiContentResolver';
import { tilPengerFormat } from '../../Shared/Scripts/Utilities/Formatters';
import { EpiContext } from '../../Shared/Scripts/Store/EpiContext/Types';
import VeilederBarnestipendDto from '../Models/VeilederBarnestipendDto';
import { beregnStotte } from '../Utilities/StottePerMndKalkulator';
import { BarnestipendSatser } from '../Utilities/BarnestipendApi';
import { VeilederBarnestipendAppState } from '../Store';

type NumberInputOwnProps = VeilederStegBaseProps & {
    inputId: string;
    value: number;
    onInputChange: (valg: number) => void;
};

type NumberInputProps = NumberInputOwnProps & MapStateToProps & MapDispatchToProps;

interface NumberInputState {
    inputValue: string;
}

class NumberInputStepStegComponent extends React.PureComponent<NumberInputProps, NumberInputState> {
    constructor(props: NumberInputProps) {
        super(props);
        this.state = {
            inputValue: props.value.toString(),
        };
        this.onInputChange = this.onInputChange.bind(this);
        this.onIncrement = this.onIncrement.bind(this);
        this.onDecrement = this.onDecrement.bind(this);
    }

    onIncrement(): void {
        this.inputChange((this.props.value + 1).toString());
    }

    onDecrement(): void {
        this.inputChange((this.props.value - 1).toString());
    }

    onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.inputChange(e.target.value);
    }

    private inputChange(newValue: string): void {
        const { onInputChange } = this.props;
        const newValueConverted = Number.parseInt(newValue, 10);

        if (!Number.isNaN(newValueConverted)) {
            const value = Math.min(Math.max(newValueConverted, 1), 9);
            onInputChange(value);
            this.setState({
                inputValue: value.toString(),
            });
        } else {
            this.setState({
                inputValue: newValue.replace(/[^\d]/, ''),
            });
        }
    }

    displayElement(verdi: number, sporsmalId: string): JSX.Element {
        return verdi ? (
            NumberInputDisplay(sporsmalId, {
                id: '',
                displayValue: verdi.toString(),
            })
        ) : (
            <div />
        );
    }

    render(): JSX.Element {
        const { value, inputId, sporsmalId, tittel, forklaring, epiContext, satser, classModifier, } = this.props;
        
        const { inputValue } = this.state;

        const valuesMap = new Map<string, string>();
        valuesMap.set('${AntallBarn}', value.toString());

        const sporsmalElementId = `${inputId}-sporsmal`;
        const stottePerMnd = satser ? tilPengerFormat(beregnStotte(value, satser), epiContext.locale) : null;

        return (
            <VeilederStegComponent
                sporsmalId={sporsmalId}
                tittel={tittel}
                forklaring={forklaring}
                inputArea={
                    <div>
                        <div className="c-veileder__content-section">
                            <div>{contentResolver(epiContext, 'antallBarnResultatStottetekstOver')}</div>
                            <div className="c-veileder__resultat-tekst">
                                {stottePerMnd}
                                <span className="c-veileder__tekst--postfix"> {epiContext.valuta}</span>
                            </div>
                            <div>
                                {contentResolver(
                                    epiContext,
                                    'antallBarnResultatStottetekstUnder',
                                    undefined,
                                    valuesMap,
                                )}
                            </div>
                        </div>
                        <h3 id={sporsmalElementId} className="c-veileder__undersporsmal">
                            {contentResolver(epiContext, 'antallBarnLedetekstSporsmal')}
                        </h3>
                        <div className="c-number-input c-number-input--minmax">
                            <button
                                className="c-number-input__button c-number-input__button--sub"
                                onClick={this.onDecrement}
                                disabled={value === 1}
                            >
                                -
                            </button>
                            <input
                                aria-labelledby={sporsmalElementId}
                                className="c-number-input__input c-number-input__input--minmax"
                                min="1"
                                max="9"
                                type="number"
                                id={inputId}
                                onChange={this.onInputChange}
                                value={inputValue}
                            />
                            <button
                                className="c-number-input__button c-number-input__button--add"
                                onClick={this.onIncrement}
                                disabled={value === 9}
                            >
                                +
                            </button>
                        </div>
                    </div>
                }
                displayElement={this.displayElement(value, sporsmalId)}
                neverCollapse={true}
                classModifier={classModifier}
                canCollapse={false}
            />
        );
    }
}

interface MapStateToProps {
    epiContext: EpiContext<VeilederBarnestipendDto>;
    satser: BarnestipendSatser | null;
}

interface MapDispatchToProps {
    stegVisningSetCollapsed: typeof setCollapsed;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function NumberInputStepStegContainer() {
    return connect(
        (state: VeilederBarnestipendAppState): MapStateToProps => {
            return {
                epiContext: state.epiContext,
                satser: state.satser,
            };
        },
        { stegVisningSetCollapsed: setCollapsed },
    )(NumberInputStepStegComponent);
}
